import { useState, useEffect } from 'react';
import { Clock, Wrench, CalendarCheck, Flag, Sun, ArrowLeft } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { useCreateShift } from '../../hooks/useCreateShift';
import { useCreateJob } from '../../hooks/useCreateJob';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { CreateBookingForm } from './CreateBookingForm';
import { CreateEventForm } from './CreateEventForm';
import { CreateTimeOffForm } from './CreateTimeOffForm';
import { DatePicker } from '../common/DatePicker';
import { TimePicker } from '../common/TimePicker';

type ScheduleItemType = 'shift' | 'job' | 'booking' | 'event' | 'timeoff' | null;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  preselect?: string | null;
}

interface TeamUser { id: string; first_name: string; last_name: string; employment_type: string; }

const OPTIONS: { key: ScheduleItemType & string; icon: typeof Clock; title: string; subtitle: string }[] = [
  { key: 'shift', icon: Clock, title: 'Shift', subtitle: 'Assign someone to work' },
  { key: 'job', icon: Wrench, title: 'Job', subtitle: 'A task with a client' },
  { key: 'booking', icon: CalendarCheck, title: 'Booking', subtitle: 'A client appointment' },
  { key: 'event', icon: Flag, title: 'Event', subtitle: 'Meeting, training, holiday' },
  { key: 'timeoff', icon: Sun, title: 'Time Off', subtitle: 'Block someone as unavailable' },
];

const TITLES: Record<string, string> = { shift: 'New Shift', job: 'New Job', booking: 'New Booking', event: 'New Event', timeoff: 'New Time Off' };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

export function AddToSchedulePanel({ isOpen, onClose, onCreated, preselect }: Props) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [selected, setSelected] = useState<ScheduleItemType>(null);
  const [users, setUsers] = useState<TeamUser[]>([]);

  // Shift form state
  const { createShift, loading: shiftLoading, error: shiftError } = useCreateShift();
  const [sUserId, setSUserId] = useState('');
  const [sDate, setSDate] = useState('');
  const [sStart, setSStart] = useState('08:00');
  const [sEnd, setSEnd] = useState('16:00');
  const [sPosition, setSPosition] = useState('');
  const [sLocation, setSLocation] = useState('');
  const [sNotes, setSNotes] = useState('');

  // Job form state
  const { createJob, loading: jobLoading, error: jobError } = useCreateJob();
  const [jTitle, setJTitle] = useState('');
  const [jClient, setJClient] = useState('');
  const [jPriority, setJPriority] = useState('medium');
  const [jAssigned, setJAssigned] = useState('');
  const [jDate, setJDate] = useState('');
  const [jTime, setJTime] = useState('');
  const [jLocation, setJLocation] = useState('');
  const [jDesc, setJDesc] = useState('');

  /* eslint-disable react-hooks/set-state-in-effect -- reset form state on open; key-remount refactor out of scope */
  useEffect(() => {
    if (isOpen) {
      setSelected((preselect as ScheduleItemType) ?? null);
      // Reset forms
      setSUserId(''); setSDate(''); setSStart('08:00'); setSEnd('16:00'); setSPosition(''); setSLocation(''); setSNotes('');
      setJTitle(''); setJClient(''); setJPriority('medium'); setJAssigned(''); setJDate(''); setJTime(''); setJLocation(''); setJDesc('');
    }
  }, [isOpen, preselect]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!isOpen || !business) return;
    supabase.from('users').select('id, first_name, last_name, employment_type').eq('business_id', business.id).eq('status', 'active')
      .then(({ data }) => setUsers(data ?? []));
  }, [isOpen, business]);

  const handleClose = () => { setSelected(null); onClose(); };
  const handleBack = () => setSelected(null);

  const submitShift = async () => {
    if (!business || !user) return;
    const ok = await createShift({ business_id: business.id, user_id: sUserId, date: sDate, start_time: sStart, end_time: sEnd, position: sPosition, location: sLocation, notes: sNotes, created_by: user.id });
    if (ok) { handleClose(); onCreated(); }
  };

  const submitJob = async () => {
    if (!business || !user) return;
    const au = users.find(u => u.id === jAssigned);
    const ok = await createJob({ business_id: business.id, title: jTitle, created_by: user.id, client_name: jClient, priority: jPriority, assigned_id: jAssigned || undefined, assigned_type: au?.employment_type, scheduled_date: jDate || undefined, scheduled_time: jTime || undefined, location: jLocation, description: jDesc });
    if (ok) { handleClose(); onCreated(); }
  };

  const panelTitle = selected ? TITLES[selected] : 'Add to Schedule';

  return (
    <SlidePanel isOpen={isOpen} onClose={handleClose} title={panelTitle} subtitle={selected ? 'Schedule \u203A ' + TITLES[selected] : 'What would you like to add?'}>
      {/* Back button */}
      {selected && (
        <button onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: palette.g400, fontFamily: 'inherit' }}>
          <ArrowLeft size={15} /> Back to options
        </button>
      )}

      {/* Step 1: Selection */}
      {!selected && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {OPTIONS.map(opt => {
            const Icon = opt.icon;
            return (
              <div key={opt.key} onClick={() => setSelected(opt.key as ScheduleItemType)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderRadius: 10, border: `1px solid ${palette.g150}`, cursor: 'pointer', transition: 'all 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.background = palette.accentWash; e.currentTarget.style.borderColor = palette.accent; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = palette.g150; }}>
                <Icon size={24} color={palette.g500} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: palette.g800 }}>{opt.title}</div>
                  <div style={{ fontSize: 13, color: palette.g400, marginTop: 1 }}>{opt.subtitle}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 2: Shift form */}
      {selected === 'shift' && (
        <div>
          {shiftError && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{shiftError}</div>}
          <div style={FIELD}><label style={LABEL}>Team member *</label><select style={INPUT} value={sUserId} onChange={e => setSUserId(e.target.value)}><option value="">Select...</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select></div>
          <div style={FIELD}><label style={LABEL}>Date *</label>
            <DatePicker value={sDate || null} onChange={(v) => setSDate(v ?? '')} />
          </div>
          <div style={{ display: 'flex', gap: 12, ...FIELD }}>
            <div style={{ flex: 1 }}><label style={LABEL}>Start *</label>
              <TimePicker value={sStart || null} onChange={(v) => setSStart(v ?? '')} step={15} />
            </div>
            <div style={{ flex: 1 }}><label style={LABEL}>End *</label>
              <TimePicker value={sEnd || null} onChange={(v) => setSEnd(v ?? '')} step={15} minTime={sStart || undefined} />
            </div>
          </div>
          <div style={FIELD}><label style={LABEL}>Position</label><input style={INPUT} value={sPosition} onChange={e => setSPosition(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Location</label><input style={INPUT} value={sLocation} onChange={e => setSLocation(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={sNotes} onChange={e => setSNotes(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
            <button onClick={handleClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={submitShift} disabled={shiftLoading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: shiftLoading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: shiftLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{shiftLoading ? 'Creating...' : 'Create Shift'}</button>
          </div>
        </div>
      )}

      {/* Step 2: Job form */}
      {selected === 'job' && (
        <div>
          {jobError && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{jobError}</div>}
          <div style={FIELD}><label style={LABEL}>Title *</label><input style={INPUT} value={jTitle} onChange={e => setJTitle(e.target.value)} placeholder="e.g. Kitchen faucet repair" /></div>
          <div style={FIELD}><label style={LABEL}>Client</label><input style={INPUT} value={jClient} onChange={e => setJClient(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Priority</label><div style={{ display: 'flex', gap: 16 }}>{['low', 'medium', 'high'].map(p => <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}><input type="radio" name="jp" checked={jPriority === p} onChange={() => setJPriority(p)} />{p}</label>)}</div></div>
          <div style={FIELD}><label style={LABEL}>Assign to</label><select style={INPUT} value={jAssigned} onChange={e => setJAssigned(e.target.value)}><option value="">Unassigned</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select></div>
          <div style={{ display: 'flex', gap: 12, ...FIELD }}>
            <div style={{ flex: 1 }}><label style={LABEL}>Date</label>
              <DatePicker value={jDate || null} onChange={(v) => setJDate(v ?? '')} />
            </div>
            <div style={{ flex: 1 }}><label style={LABEL}>Time</label>
              <TimePicker value={jTime || null} onChange={(v) => setJTime(v ?? '')} step={15} />
            </div>
          </div>
          <div style={FIELD}><label style={LABEL}>Location</label><input style={INPUT} value={jLocation} onChange={e => setJLocation(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Description</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={jDesc} onChange={e => setJDesc(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
            <button onClick={handleClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={submitJob} disabled={jobLoading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: jobLoading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: jobLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{jobLoading ? 'Creating...' : 'Create Job'}</button>
          </div>
        </div>
      )}

      {/* Step 2: Booking */}
      {selected === 'booking' && <CreateBookingForm onCreated={() => { handleClose(); onCreated(); }} onCancel={handleClose} />}

      {/* Step 2: Event */}
      {selected === 'event' && <CreateEventForm onCreated={() => { handleClose(); onCreated(); }} onCancel={handleClose} />}

      {/* Step 2: Time Off */}
      {selected === 'timeoff' && <CreateTimeOffForm onCreated={() => { handleClose(); onCreated(); }} onCancel={handleClose} />}
    </SlidePanel>
  );
}
